import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Card, Table, Tag, Button, Dropdown, Menu, Tooltip, Spin, message, Modal, Row, Col } from "antd";
import {
  ArrowLeftOutlined,
  FilePdfOutlined,
  DownloadOutlined,
  MoreOutlined,
  DollarOutlined,
  CalendarOutlined,
  CommentOutlined,
  EditOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  VideoCameraOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  EyeOutlined,
  LinkOutlined,
  FolderOutlined,
} from "@ant-design/icons";
import { JobDetailsWrapper } from "./AdminJobDetails.style";
import axios from "axios";
import { API_CONST } from "../../../../const";

const AdminJobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState([]);
  
  // Get back navigation info from state, default to Job Requirements
  const backPath = location.state?.from || "/admin/job-requirments";
  const backLabel = location.state?.fromLabel || "Back to Job Requirements";

  const fetchSubmittedProfiles = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_JOB_APPLICATIONS,
        { jobId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.applications) {
        const mappedProfiles = response.data.applications.map((app) => {
          return {
            id: app.applicationId,
            talentId: app.talentId,
            name: app.talent?.name || 'Unknown',
            email: app.talent?.email || '-',
            phone: app.talent?.phone || '-',
            role: app.talent?.role || 'N/A',
            primarySkills: app.talent?.primarySkills || [],
            skills: app.talent?.skills || [],
            monthlyRate: formatCurrency(app.talent?.clientRate),
            clientRate: formatCurrency(app.talent?.clientRate),
            experience: app.talent?.experience || 'N/A',
            noticePeriod: app.talent?.noticePeriod || 'N/A',
            resumeUrl: app.talent?.resumeUrl,
            status: app.status,
            submittedAt: app.submittedAt,
            partnerOrganization: app.partnerOrganization,
          };
        });
        setProfiles(mappedProfiles);
      }
    } catch (error) {
      console.error("Error fetching submitted profiles:", error);
    }
  };

  useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");
        const response = await axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        if (response.data.job) {
          setJobData(response.data.job);
          await fetchSubmittedProfiles();
        }
      } catch (error) {
        console.error("Error fetching job details:", error);
        message.error("Failed to load job details");
      } finally {
        setLoading(false);
      }
    };

    if (jobId) {
      fetchJobDetails();
    }
  }, [jobId]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `₹ ${Number(amount).toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <JobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>Loading job details...</p>
        </div>
      </JobDetailsWrapper>
    );
  }

  if (!jobData) {
    return (
      <JobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Job not found</h2>
          <Button type="primary" onClick={() => navigate(backPath)}>
            {backLabel}
          </Button>
        </div>
      </JobDetailsWrapper>
    );
  }

  const handleEditJob = () => {
    navigate(`/admin/job-requirments/edit-job/${jobId}`);
  };

  const handleViewResume = async (resumeUrl) => {
    if (!resumeUrl) {
      message.warning("No resume available for this profile");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        window.open(response.data.downloadUrl, "_blank");
      } else {
        message.error("Could not get download URL");
      }
    } catch (error) {
      console.error("Error getting resume URL:", error);
      message.error("Failed to get resume URL");
    }
  };

  const updateApplicationStatus = async (applicationId, status) => {
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_CONST.UPDATE_JOB_APPLICATION}/${applicationId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return true;
    } catch (error) {
      console.error("Error updating application status:", error);
      message.error("Failed to update status");
      return false;
    }
  };

  const handleAction = (profileId, action) => {
    const profile = profiles.find((p) => p.id === profileId);
    const profileName = profile?.name || "this profile";

    const actionLabels = {
      interview_scheduled: "Schedule Interview",
      hired: "Hire",
      rejected: "Reject",
    };
    const actionMessages = {
      interview_scheduled: `Schedule an interview for ${profileName}?`,
      hired: `Mark ${profileName} as hired?`,
      rejected: `Reject ${profileName}? This action cannot be undone.`,
    };

    Modal.confirm({
      title: actionLabels[action] || "Confirm Action",
      content: actionMessages[action] || `Are you sure you want to update the status?`,
      okText: actionLabels[action] || "Confirm",
      okButtonProps: action === "rejected" ? { danger: true } : {},
      cancelText: "Cancel",
      onOk: async () => {
        const success = await updateApplicationStatus(profileId, action);
        if (success) {
          setProfiles(
            profiles.map((p) =>
              p.id === profileId ? { ...p, status: action } : p
            )
          );
          message.success(`Status updated to ${action.replace(/_/g, " ")}`);
        }
      },
    });
  };

  const getActionMenu = (record) => {
    const isHired = record.status === "hired";
    const isRejected = record.status === "rejected";
    const isInterviewScheduled = record.status === "interview_scheduled";
    
    return (
      <Menu>
        <Menu.Item
          key="interview"
          icon={<VideoCameraOutlined />}
          disabled={isHired || isRejected || isInterviewScheduled}
          onClick={() => handleAction(record.id, "interview_scheduled")}
        >
          Schedule Interview
        </Menu.Item>
        <Menu.Item
          key="hire"
          icon={<CheckCircleOutlined />}
          disabled={isHired || isRejected}
          onClick={() => handleAction(record.id, "hired")}
        >
          Hire
        </Menu.Item>
        <Menu.Item
          key="reject"
          icon={<CloseCircleOutlined />}
          danger
          disabled={isHired || isRejected}
          onClick={() => handleAction(record.id, "rejected")}
        >
          Reject
        </Menu.Item>
      </Menu>
    );
  };

  const columns = [
    {
      title: "Resume",
      dataIndex: "resumeUrl",
      key: "resume",
      width: 80,
      render: (url) => (
        <Tooltip title="View Resume">
          <FilePdfOutlined
            style={{ fontSize: 24, color: "#ff4d4f", cursor: "pointer" }}
            onClick={() => handleViewResume(url)}
          />
        </Tooltip>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 150,
      render: (name, record) => (
        <a
          style={{ fontWeight: 500, color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/talent-profiles/details/${record.talentId}`, {
            state: { from: `/admin/job-requirments/job-details/${jobId}`, fromLabel: "Back to Job Details" }
          })}
        >
          {name}
        </a>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
    },
    {
      title: "Primary Skills",
      dataIndex: "primarySkills",
      key: "primarySkills",
      width: 300,
      render: (skills) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(skills || []).map((skill, index) => (
            <Tag key={index} color="cyan" style={{ margin: "2px 0" }}>
              {typeof skill === 'object' ? `${skill.skill} - ${skill.level}` : skill}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: 120,
      render: (rate) => <span style={{ fontWeight: 500 }}>{rate}</span>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 100,
    },
    {
      title: "Notice Period",
      dataIndex: "noticePeriod",
      key: "noticePeriod",
      width: 120,
    },
    {
      title: "Action",
      key: "action",
      width: 150,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Tooltip title="Schedule Interview">
            <Button
              type="primary"
              icon={<VideoCameraOutlined />}
              size="small"
              onClick={() => handleAction(record.id, "interviewing")}
            />
          </Tooltip>
          <Tooltip title="Download Resume">
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleViewResume(record.resumeUrl)}
            />
          </Tooltip>
          <Dropdown overlay={getActionMenu(record)} trigger={["click"]}>
            <Button icon={<MoreOutlined />} size="small" />
          </Dropdown>
        </div>
      ),
    },
  ];

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'green';
      case 'closed': return 'red';
      case 'filled': return 'blue';
      case 'draft': return 'orange';
      default: return 'default';
    }
  };

  return (
    <JobDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(backPath)}
          className="back-button"
        >
          {backLabel}
        </Button>
        <div className="title-row">
          <h2>Job Details</h2>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEditJob}
            style={{
              backgroundColor: "#00d9a9",
              borderColor: "#00d9a9",
              height: "40px",
            }}
          >
            Edit Job
          </Button>
        </div>
      </div>

      <Card className="job-summary-card">
        <div className="job-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="job-id">Job ID: {jobData.id}</div>
            <h2 className="job-title">{jobData.role}</h2>
            <div className="job-meta">
              <Tag color={getStatusColor(jobData.status)}>{jobData.status?.toUpperCase()}</Tag>
              <Tag color="blue">{jobData.engagementType}</Tag>
              <Tag color="cyan" icon={<EnvironmentOutlined />}>{jobData.reportingLocation}</Tag>
              <Tag color="green" icon={<TeamOutlined />}>Requirements: {jobData.numberOfRequirements}</Tag>
              {jobData.customerOrganization && (
                <Tag 
                  color="purple" 
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/admin/organization/${encodeURIComponent(jobData.customerOrganization)}`, {
                    state: { from: `/admin/job-requirments/job-details/${jobId}`, fromLabel: "Back to Job Details" }
                  })}
                >
                  Customer: {jobData.customerOrganization}
                </Tag>
              )}
            </div>
          </div>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/job-requirments/job-full-details/${jobId}`)}
            style={{ 
              color: "#1890ff", 
              fontWeight: 500,
              fontSize: 14,
              padding: 0,
            }}
          >
            Detailed View
          </Button>
        </div>

        <div className="metrics-row">
          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#e6f7ff" }}>
              <DollarOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </div>
            <div className="metric-info">
              <h3>{formatCurrency(jobData.budget)}</h3>
              <p>{jobData.budgetPeriod || 'Per Month'}</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#fff7e6" }}>
              <CalendarOutlined style={{ fontSize: 24, color: "#fa8c16" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.monthsOfEngagement || '-'}</h3>
              <p>Engagement Duration</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#f6ffed" }}>
              <ClockCircleOutlined style={{ fontSize: 24, color: "#52c41a" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.experienceRange || '-'}</h3>
              <p>Experience Required</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#fff1f0" }}>
              <CommentOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.communication || '-'}</h3>
              <p>Communication Level</p>
            </div>
          </div>
        </div>

        {(jobData.engagement || jobData.project) && (
          <Row gutter={[24, 12]} style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
            <Col xs={24} sm={12}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <LinkOutlined style={{ color: "#1890ff", fontSize: 14 }} />
                <span style={{ fontWeight: 600, color: "#595959", fontSize: 13 }}>Engagement:</span>
                {jobData.engagement ? (
                  <span
                    style={{ color: "#1890ff", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                    onClick={() => navigate(`/admin/engagements/details/${jobData.engagementId}`)}
                  >
                    {jobData.engagement.title}
                  </span>
                ) : (
                  <span style={{ color: "#8c8c8c", fontSize: 13 }}>NA</span>
                )}
              </div>
            </Col>
            {jobData.project && (
              <Col xs={24} sm={12}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <FolderOutlined style={{ color: "#52c41a", fontSize: 14 }} />
                  <span style={{ fontWeight: 600, color: "#595959", fontSize: 13 }}>Project:</span>
                  <span
                    style={{ color: "#1890ff", cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                    onClick={() => navigate(`/admin/projects/details/${jobData.projectId}`)}
                  >
                    {jobData.project.title}
                  </span>
                </div>
              </Col>
            )}
          </Row>
        )}

        <Row gutter={[24, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Primary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.primarySkills || []).map((skill, index) => (
                  <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Secondary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.secondarySkills || []).map((skill, index) => (
                  <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
                {(!jobData.secondarySkills || jobData.secondarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Start Date:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.tentativeStartDate)}</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Created On:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.createdAt)}</div>
            </div>
          </Col>
        </Row>
      </Card>

      <div className="profiles-table-card">
        <h3>Submitted Profiles ({profiles.length})</h3>
        {profiles.length > 0 ? (
          <Table
            columns={columns}
            dataSource={profiles}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            scroll={{ x: 1400 }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#8c8c8c" }}>
            <p>No profiles submitted yet for this job.</p>
          </div>
        )}
      </div>
    </JobDetailsWrapper>
  );
};

export default AdminJobDetails;
